import React, { useContext, useEffect, useState } from 'react'
import type Svalna from '../../../@types'
import { setToken } from '../../../utils/auth'
import {
  ErrorType,
  useLoginWithFortnoxMutation,
} from '../../../graphql/generated'
import { UserContext } from '../../../context/UserContext'
import { captureException } from '../../../utils/Sentry'
import { isResponseWithFieldError } from '../../../utils/graphqlResponseType'

export enum FortnoxErrorType {
  MissingLicense,
  OrgNotRegistered,
  NoError,
  UserNotRegistered,
  NotApproved,
  RegistrationCodeExpired,
  RegistrationCodeNotFound, // no entry in the db with this code in the db
  RegistrationCodeMissing, // no code in the request sent by the user
}

interface IUserContext {
  setCode: (code: string) => void
  loading: boolean
  setLoading: (loading: boolean) => void
  fortnoxErrorType: FortnoxErrorType
  setFortnoxErrorType: (errorType: FortnoxErrorType) => void
  error: string
  setError: (error: string) => void
  setOrgRegistrationCode: (registrationCode: string | null) => void
  orgRegistrationCode: string | null
  setUserRegistrationCode: (registrationCode: string | null) => void
  userRegistrationCode: string | null
}

const initialState: IUserContext = {
  setCode: () => {},
  loading: true,
  setLoading: () => {},
  fortnoxErrorType: FortnoxErrorType.NoError,
  setFortnoxErrorType: () => {},
  error: '',
  setError: () => {},
  setOrgRegistrationCode: () => {},
  orgRegistrationCode: '',
  setUserRegistrationCode: () => {},
  userRegistrationCode: '',
}

export const FortnoxLoginContext =
  React.createContext<IUserContext>(initialState)

const { Provider } = FortnoxLoginContext

export function FortnoxLoginProvider({
  children,
}: Svalna.PropWithChildren): React.JSX.Element {
  const { setLoggedIn } = useContext(UserContext)
  const [loginWithFortnox] = useLoginWithFortnoxMutation({})
  const [code, setCode] = useState('')
  const [orgRegistrationCode, setOrgRegistrationCode] = useState<string | null>(
    null,
  )
  const [userRegistrationCode, setUserRegistrationCode] = useState<
    string | null
  >(null)
  const [loading, setLoading] = useState(true)
  const [fortnoxErrorType, setFortnoxErrorType] = useState(
    FortnoxErrorType.NoError,
  )
  const [error, setError] = useState('')

  useEffect(() => {
    if (code) {
      loginWithFortnox({
        variables: {
          code: code,
          redirectUri: window.location.origin + window.location.pathname,
          registrationCode: userRegistrationCode ?? orgRegistrationCode,
        },
      })
        .then((res) => {
          if (res.data) {
            if (isResponseWithFieldError(res.data.loginWithFortnox)) {
              let isNotHandled = false
              let errorMsgs = 'Field error when login with fortnox'
              res.data.loginWithFortnox.errors.forEach((e) => {
                errorMsgs += `- ${e.field} (${e.errorType}): ${e.message}/n`
                switch (e.errorType) {
                  case ErrorType.NoOrg:
                    setFortnoxErrorType(FortnoxErrorType.OrgNotRegistered)
                    break
                  case ErrorType.NotApproved:
                    setFortnoxErrorType(FortnoxErrorType.NotApproved)
                    break
                  case ErrorType.UserNotRegistered:
                    setFortnoxErrorType(FortnoxErrorType.UserNotRegistered)
                    break
                  case ErrorType.RegistrationCodeExpired:
                    setFortnoxErrorType(
                      FortnoxErrorType.RegistrationCodeExpired,
                    )
                    break
                  case ErrorType.RegistrationCodeNotFound:
                    setFortnoxErrorType(
                      FortnoxErrorType.RegistrationCodeNotFound,
                    )
                    break
                  case ErrorType.RegistrationCodeMissing:
                    setFortnoxErrorType(
                      FortnoxErrorType.RegistrationCodeMissing,
                    )
                    break
                  default:
                    isNotHandled = true
                    setError(e.message)
                }
              })
              captureException(
                new Error(errorMsgs),
                isNotHandled ? 'error' : 'warning',
              )
              setLoading(false)
              return
            }

            if (res.data.loginWithFortnox.authentication) {
              const { token, expiresAt, refreshToken } =
                res.data.loginWithFortnox.authentication
              setToken(token, refreshToken, expiresAt)
              setLoggedIn(true)
              return
            }
          }
          // we should never reach this point as if the data were not set an error should have been thrown
          // and if the data were set we should have gone in one of the if statements above
          throw new Error('Badly formed data in response to loginWithFortnox')
        })
        .catch((er) => {
          captureException(er)
          setError('Något gick fel')
          setLoading(false)
        })
    }
  }, [
    code,
    loginWithFortnox,
    setLoggedIn,
    userRegistrationCode,
    orgRegistrationCode,
  ])

  return (
    <Provider
      value={{
        setCode,
        fortnoxErrorType,
        loading,
        setFortnoxErrorType,
        setLoading,
        error,
        setError,
        setOrgRegistrationCode,
        orgRegistrationCode,
        setUserRegistrationCode,
        userRegistrationCode,
      }}
    >
      {children}
    </Provider>
  )
}
